import { captureException } from "@sentry/nextjs";
import Script from "next/script";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useIsDark } from "../../../lib/hooks/theme";

export function useTurnstile(locale?: string) {
  const isDark = useIsDark();
  const [token, setToken] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [scriptReady, setScriptReady] = useState(false);

  const reset = useCallback(() => {
    setToken("");
    if (typeof window === "undefined" || !window.turnstile) return;
    if (!containerRef.current) return;

    if (widgetIdRef.current) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        return;
      } catch {
        widgetIdRef.current = null;
      }
    }

    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
        callback: (t: string) => setToken(t),
        "expired-callback": () => setToken(""),
        "error-callback": () => setToken(""),
        theme: isDark ? "dark" : "light",
        language: locale,
      });
    } catch (e) {
      captureException(e);
    }
  }, [isDark, locale]);

  useEffect(() => {
    if (!scriptReady || !window.turnstile) return;

    if (widgetIdRef.current) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch (error) {
        captureException(error);
      }
      widgetIdRef.current = null;
    }

    const frame = requestAnimationFrame(reset);
    return () => cancelAnimationFrame(frame);
  }, [scriptReady, reset]);

  const ScriptTag = (
    <Script
      src="https://challenges.cloudflare.com/turnstile/v0/api.js"
      strategy="afterInteractive"
      onLoad={() => setScriptReady(true)}
      onError={(e) => captureException(e)}
    />
  );

  return { token, reset, containerRef, ScriptTag };
}
